/**
 * SwotControls — inline, status-aware controls for the Synrgy-only SWOT Call
 * prospect sub-stage. Rendered in the lifecycle band's "next action" slot while
 * the account is in the LEAD phase and the tenant has the `swotCall` flag on.
 *
 * Flow (mirrors the synrgyinfinity proof-of-concept):
 *   - no decision yet   → admins schedule the call directly ("Schedule SWOT
 *                         call") or skip it; brokers go through the request
 *                         pipeline ("Set up SWOT call" → Request call / Request
 *                         to skip popup) and wait for an admin.
 *   - requested         → admin schedules a date; broker awaits
 *   - skip requested    → admin confirms the skip or schedules a call; broker awaits
 *   - scheduled         → admin marks completed / reschedules / cancels; broker awaits
 *   - completed/cancelled/skipped → "Generate RFC" returns (the proposal now
 *                         comes later, after the RFC is approved)
 *
 * Scheduling / completing / cancelling / skipping are admin-only; requesting a
 * call, requesting to skip, and generating the RFC are available to the
 * owning broker (and admins).
 *
 * All user-facing copy interpolates the tenant's configured label for the
 * SWOT_CALL sub-stage (accounts-view editor), so renaming the stage requires
 * no code change here. Falls back to "SWOT Call" when unconfigured.
 */

import React from 'react';
import {Calendar, CalendarClock, Check, FileText, SkipForward} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Input} from '@common-web/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import type {AccountsSwotStatus} from '@api/generated/client';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';
import {findPositionBySystemSubStage} from '@common/accountsView';

export interface SwotControlsProps {
  companyName: string;
  status: AccountsSwotStatus | null;
  scheduledDate: Date | string | null;
  /** Caller may schedule / complete / cancel / skip (client admin or internal admin). */
  isAdmin: boolean;
  /** Caller may request a call / request a skip / generate a proposal (owning broker or admin). */
  canAct: boolean;
  isPending: boolean;
  onRequest: () => void;
  /** Admin-only direct skip (confirms a skip request or skips outright). */
  onSkip: () => void;
  /** Broker-facing "request to skip" — admin must then confirm. */
  onRequestSkip: () => void;
  onSchedule: (date: string) => void;
  onComplete: () => void;
  onCancel: () => void;
  /** Once the SWOT call is resolved the next move is generating the RFC. */
  onGenerateRfc: () => void;
  /** Whether an RFC already exists — hides the "Generate RFC" CTA. */
  hasRfc: boolean;
}

function formatScheduled(date: Date | string | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function SwotControls({
  companyName,
  status,
  scheduledDate,
  isAdmin,
  canAct,
  isPending,
  onRequest,
  onSkip,
  onRequestSkip,
  onSchedule,
  onComplete,
  onCancel,
  onGenerateRfc,
  hasRfc,
}: SwotControlsProps): React.ReactElement {
  const [decisionOpen, setDecisionOpen] = React.useState(false);
  const [scheduleOpen, setScheduleOpen] = React.useState(false);

  // The tenant's configured display name for the SWOT_CALL sub-stage — copy
  // follows whatever the stage is renamed to in the accounts-view editor.
  const config = useAccountsViewConfig();
  const callLabel =
    findPositionBySystemSubStage(config, 'SWOT_CALL')?.subStage.label ?? 'SWOT Call';

  const dateLabel = formatScheduled(scheduledDate);

  const controls = (() => {
    // No decision recorded yet (account still on the Prospect step).
    if (!status) {
      if (!canAct) return <span className='text-xs text-muted-foreground'>awaiting</span>;
      // Admins skip the broker request pipeline entirely: they schedule the
      // call directly (or skip it for smaller groups). Brokers still "request"
      // a call and wait for an admin to pick a date.
      if (isAdmin) {
        return (
          <span className='flex flex-wrap items-center gap-2'>
            <Button size='sm' disabled={isPending} onClick={() => setScheduleOpen(true)}>
              <Calendar className='mr-1.5 h-4 w-4' />
              Schedule {callLabel}
            </Button>
            <Button size='sm' variant='ghost' disabled={isPending} onClick={onSkip}>
              <SkipForward className='mr-1.5 h-4 w-4' />
              Skip
            </Button>
          </span>
        );
      }
      return (
        <Button size='sm' disabled={isPending} onClick={() => setDecisionOpen(true)}>
          <CalendarClock className='mr-1.5 h-4 w-4' />
          Set up {callLabel}
        </Button>
      );
    }

    if (status === 'REQUESTED') {
      return (
        <span className='flex items-center gap-2'>
          <Badge variant='warning'>Requested</Badge>
          {isAdmin ? (
            <Button size='sm' disabled={isPending} onClick={() => setScheduleOpen(true)}>
              <Calendar className='mr-1.5 h-4 w-4' />
              Schedule
            </Button>
          ) : (
            <span className='text-xs text-muted-foreground'>awaiting scheduling</span>
          )}
        </span>
      );
    }

    if (status === 'SKIP_REQUESTED') {
      return (
        <span className='flex flex-wrap items-center gap-2'>
          <Badge variant='warning'>Skip requested</Badge>
          {isAdmin ? (
            <>
              <Button size='sm' disabled={isPending} onClick={onSkip}>
                <SkipForward className='mr-1.5 h-4 w-4' />
                Confirm skip
              </Button>
              <Button
                size='sm'
                variant='outline'
                disabled={isPending}
                onClick={() => setScheduleOpen(true)}
              >
                <Calendar className='mr-1.5 h-4 w-4' />
                Schedule instead
              </Button>
            </>
          ) : (
            <span className='text-xs text-muted-foreground'>awaiting admin</span>
          )}
        </span>
      );
    }

    if (status === 'SCHEDULED') {
      return (
        <span className='flex flex-wrap items-center gap-2'>
          <Badge variant='info'>Scheduled{dateLabel ? ` · ${dateLabel}` : ''}</Badge>
          {isAdmin ? (
            <>
              <Button size='sm' disabled={isPending} onClick={onComplete}>
                <Check className='mr-1.5 h-4 w-4' />
                Mark completed
              </Button>
              <Button
                size='sm'
                variant='outline'
                disabled={isPending}
                onClick={() => setScheduleOpen(true)}
              >
                Reschedule
              </Button>
              <Button size='sm' variant='ghost' disabled={isPending} onClick={onCancel}>
                Cancel
              </Button>
            </>
          ) : (
            <span className='text-xs text-muted-foreground'>scheduled</span>
          )}
        </span>
      );
    }

    // Resolved — completed / cancelled / skipped → the next move is the RFC.
    // (The proposal now comes later, after the RFC is approved.)
    const resolvedBadge =
      status === 'COMPLETED' ? (
        <Badge variant='success'>Completed</Badge>
      ) : status === 'CANCELLED' ? (
        <Badge variant='secondary'>Cancelled</Badge>
      ) : (
        <Badge variant='secondary'>Skipped</Badge>
      );
    return (
      <span className='flex flex-wrap items-center gap-2'>
        {resolvedBadge}
        {canAct && !hasRfc ? (
          <Button size='sm' disabled={isPending} onClick={onGenerateRfc}>
            <FileText className='mr-1.5 h-4 w-4' />
            Generate RFC
          </Button>
        ) : null}
      </span>
    );
  })();

  return (
    <>
      {controls}

      <SwotDecisionDialog
        open={decisionOpen}
        onOpenChange={setDecisionOpen}
        companyName={companyName}
        callLabel={callLabel}
        onRequest={() => {
          setDecisionOpen(false);
          onRequest();
        }}
        onRequestSkip={() => {
          setDecisionOpen(false);
          // Brokers can only request a skip for an admin to confirm — matching
          // the request-a-call → admin-schedules flow. Admins never reach this
          // dialog; they schedule or skip directly from the controls above.
          onRequestSkip();
        }}
      />

      <SwotScheduleDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        companyName={companyName}
        callLabel={callLabel}
        initialDate={status === 'SCHEDULED' && scheduledDate ? toDateInput(scheduledDate) : ''}
        onSubmit={(date) => {
          setScheduleOpen(false);
          onSchedule(date);
        }}
      />
    </>
  );
}

function toDateInput(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toISOString().slice(0, 10);
}

function SwotDecisionDialog({
  open,
  onOpenChange,
  companyName,
  callLabel,
  onRequest,
  onRequestSkip,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyName: string;
  callLabel: string;
  onRequest: () => void;
  onRequestSkip: () => void;
}): React.ReactElement {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>{callLabel}</DialogTitle>
          <DialogDescription>
            Does <span className='font-semibold'>{companyName}</span> need a {callLabel}?
            Requesting one notifies the team to schedule a date. For smaller groups you can request
            to skip it — an admin will confirm.
          </DialogDescription>
        </DialogHeader>
        <div className='flex flex-col gap-2'>
          <Button onClick={onRequest}>
            <CalendarClock className='mr-1.5 h-4 w-4' />
            Request {callLabel}
          </Button>
          <Button variant='outline' onClick={onRequestSkip}>
            <SkipForward className='mr-1.5 h-4 w-4' />
            Request to skip {callLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SwotScheduleDialog({
  open,
  onOpenChange,
  companyName,
  callLabel,
  initialDate,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyName: string;
  callLabel: string;
  initialDate: string;
  onSubmit: (date: string) => void;
}): React.ReactElement {
  const [date, setDate] = React.useState(initialDate);
  const isReschedule = !!initialDate;

  // Reset the field whenever the dialog (re)opens so it reflects the current
  // scheduled date rather than a stale local value.
  React.useEffect(() => {
    if (open) setDate(initialDate);
  }, [open, initialDate]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-sm'>
        <DialogHeader>
          <DialogTitle>
            {isReschedule ? `Reschedule ${callLabel}` : `Schedule ${callLabel}`}
          </DialogTitle>
          <DialogDescription>
            Pick the date the {callLabel} is scheduled for{' '}
            <span className='font-medium'>{companyName}</span>. The broker is notified of the date.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-1.5'>
          <label className='text-sm font-medium'>{callLabel} date</label>
          <Input type='date' value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!date} onClick={() => onSubmit(date)}>
            <Calendar className='mr-1.5 h-4 w-4' />
            {isReschedule ? 'Reschedule' : 'Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
